import React from 'react';
import { css, Global } from '@emotion/react';
import { GlobalStyle as NafGlobalStyle } from '@naf/global-style';
import { breakpoints } from '@naf/theme';
import { ChatStyles } from './ChatStyles';

// NOA-3189: the base reset from `@naf/global-style` is a styled-components rule-set — it carries
// theme interpolations (`props => props.theme.…`) *and* `&`-nested selectors (`a { color; &:hover }`).
// Feeding that raw rule-set to Emotion's <Global> dropped every nested block (stylis has no parent
// selector to resolve `&` against at global scope), so link colours never applied and links fell
// back to the browser-default blue. Render the package's own `createGlobalStyle` component instead:
// it resolves the theme (via the styled-components ThemeProvider it already sits under in ThemedApp)
// and the nesting exactly as authored.
//
// The app-specific globals below are flat, theme-free CSS, so Emotion's <Global> handles them fine.
const appGlobalStyles = css`
  ${ChatStyles}

  body {
    overflow-x: clip;
  }

  /*
   * The base stylesheet colours every anchor — a { color: defaultText } and
   * a:hover { color: textLink } — which is right for a link in a sentence and wrong for
   * every surface that is a link: a card, a tile, the footer logo. Those expect to
   * inherit the colour of whatever they sit on, and an inherited value loses to a
   * declared one however weak its selector, so they came out near-black on the dark
   * footer and green on hover.
   *
   * "html a" outranks "a" (and "html a:hover" outranks "a:hover") whichever stylesheet
   * is injected first, so this hands the colour back to the element. A link that wants
   * the link colour says so itself — the article body does, in StyledArticle.
   */
  html a,
  html a:visited,
  html a:hover {
    color: inherit;
  }

  /* Hide native cookie banner */
  #cookiebanner {
    display: none !important;
  }

  html {
    transition:
      color 0.2s linear,
      background-color 0.2s linear;
    overflow-x: clip;
  }

  /*
   * Anchor targets (#hash links, table of contents) must land below the sticky navbar, which
   * ends 86px from the top on desktop and 68px on the mobile navbar. :where() keeps this at
   * zero specificity so a component's own scroll-margin still wins.
   */
  :where([id]) {
    scroll-margin-top: 104px;

    @media (max-width: ${breakpoints.l}) {
      scroll-margin-top: 80px;
    }
  }
`;

export const GlobalStyle = () => (
  <>
    <NafGlobalStyle />
    <Global styles={appGlobalStyles} />
  </>
);
